import * as AWS from 'aws-sdk/global';
import S3 from 'aws-sdk/clients/s3';

import { sanitizeS3Key } from '@trqk/utils';

const oneMb = 1024 * 1024;

interface UploadFileInput {
    bindManagedUpload?: BindManagedUpload;
    bucketName: string;
    bucketRegion: string;
    file: File | S3UploadFile;
    generateS3Key(file: File): string;
    handleProgress?(currentProgress: S3UploadProgress): void;
    identityPoolId: string;
    key?: string;
    taskId?: Maybe<string>;
}

export const uploadFile = async ({
    bindManagedUpload,
    bucketName,
    bucketRegion,
    file,
    generateS3Key,
    handleProgress,
    identityPoolId,
    key,
    taskId = null,
}: UploadFileInput): Promise<UploadToS3Result> => {
    const credentials = new AWS.CognitoIdentityCredentials({
        IdentityPoolId: identityPoolId,
    });

    AWS.config.update({
        region: bucketRegion,
        credentials,
        retryDelayOptions: {
            base: 1000,
        },
    });

    const s3 = new S3({
        apiVersion: '2006-03-01',
        params: { Bucket: bucketName },
        signatureVersion: 'v4',
        region: bucketRegion,
        credentials,
    });

    // Special characters aren't allowed in S3 keys
    const finalKey = sanitizeS3Key(key || generateS3Key(file));

    const params = {
        Bucket: bucketName,
        Body: file,
        Key: finalKey,
    };
    const options = { partSize: 10 * oneMb, queueSize: 4 };
    // S3.upload returns a AWS.S3.ManagedUpload instance
    // https://docs.aws.amazon.com/AWSJavaScriptSDK/latest/AWS/S3/ManagedUpload.html
    const managedUpload = s3.upload(params, options);

    // If there is a context.bindManagedUpload, call it with the managedUpload instance.
    // This will allow the context to get a reference to the upload so it can call its methods,
    // such as managedUpload.abort().
    if (bindManagedUpload) {
        bindManagedUpload(managedUpload);
    }

    if (handleProgress) {
        managedUpload.on('httpUploadProgress', handleProgress);
    }

    const { Location, Key } = await managedUpload.promise();

    return {
        aborted: false,
        completed: true,
        error: null,
        result: {
            filename: file.name,
            key: Key,
            location: Location,
        },
        taskId,
    };
};
